import { get, isEmpty } from "lodash";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { DynamicIcon, IconName } from "lucide-react/dynamic";
import { FC, PropsWithChildren, ReactNode, useMemo } from "react";

import BuilderLogo from "@/assets/logos/builder-logo.svg?react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TabDetails } from "@/components/web-experience/tab-details";
import {
  SKUPageContentGenericTabIcon,
  SKUPageContentTab,
  SKUPageContentTabType,
} from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";

import { useCMSTabContext } from "./cms-tab-context";

type Props = PropsWithChildren<{
  options: SKUPageContentTab["options"];
}>;

const getDefaultTabIcon = (type: SKUPageContentTabType): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.MUSIC_TAB:
      return "audio-lines";
    case SKUPageContentTabType.PRODUCT_TAB:
      return "tag";
    case SKUPageContentTabType.GALLERY_TAB:
      return "gallery-vertical";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "builder-io";
    default:
      return;
  }
};

const getTabIcon = (icon: SKUPageContentGenericTabIcon): ReactNode => {
  if (icon === "builder-io") {
    return <BuilderLogo className="size-5" />;
  }

  return <DynamicIcon name={icon as IconName} className="size-5" />;
};

const getDefaultTabTitle = (
  type: SKUPageContentTabType,
): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.MUSIC_TAB:
      return "Audio";
    case SKUPageContentTabType.PRODUCT_TAB:
      return "Product";
    case SKUPageContentTabType.GALLERY_TAB:
      return "Gallery";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "Builder";
    default:
      return;
  }
};

const getTabTypeLabel = (type: SKUPageContentTabType): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.PRODUCT_TAB:
      return "Product Tab";
    case SKUPageContentTabType.MUSIC_TAB:
      return "Audio Tab";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "Builder.io Tab";
    case SKUPageContentTabType.GALLERY_TAB:
      return "Gallery Tab";
    default:
      return;
  }
};

export const CMSTab: FC<Props> = ({ children, options }) => {
  const {
    isExpanded,
    canMoveUp,
    canMoveDown,
    canDelete,
    onMoveUp,
    onMoveDown,
    onExpand,
    onDelete,
    tabType,
  } = useCMSTabContext();
  const { openDialog } = useDialogState();

  const defaultIcon = getDefaultTabIcon(tabType);
  const defaultTitle = getDefaultTabTitle(tabType);

  const icon = useMemo(() => {
    const value = get(options, "tab_icon");
    if (isEmpty(value)) {
      return defaultIcon;
    }

    return value;
  }, [options, defaultIcon]);

  const title = useMemo(() => {
    const value = get(options, "tab_title");
    if (isEmpty(value)) {
      return defaultTitle;
    }

    return value;
  }, [options, defaultTitle]);

  const tabTypeLabel = getTabTypeLabel(tabType);

  return (
    <div className="first:border-t border-b">
      <div className="flex gap-3 w-full py-5">
        <div className="flex gap-2 items-center flex-grow min-w-0">
          {getTabIcon(icon as SKUPageContentGenericTabIcon)}
          <p className="text-lg font-medium flex-grow min-w-0 truncate">
            {title}
          </p>
          {tabTypeLabel && <Badge variant="default">{tabTypeLabel}</Badge>}
        </div>
        <div className="flex gap-2">
          <Button
            size="icon"
            variant="outline"
            disabled={!canMoveUp}
            onClick={onMoveUp}
            type="button"
          >
            <ArrowUp />
          </Button>
          <Button
            size="icon"
            variant="outline"
            disabled={!canMoveDown}
            onClick={onMoveDown}
            type="button"
          >
            <ArrowDown />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={!canDelete}
            onClick={() => {
              openDialog({
                name: "delete-cms-tab",
                params: { onDelete },
              });
            }}
          >
            <Trash2 className="size-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={onExpand}
            className="w-28"
            type="button"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="size-4" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="size-4" />
                Expand
              </>
            )}
          </Button>
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="pb-5">
            <TabDetails
              defaultTitle={defaultTitle}
              defaultIcon={defaultIcon}
              options={options}
            />
          </div>
          <div className="pb-5">{children}</div>
        </>
      )}
    </div>
  );
};
